import { userHasNetwork } from "@/lib/networkAccess"

import {
  ADW_COLUMN_STORAGE_KEY,
  ADW_COLUMNS,
  ADW_VISIBILITY_STORAGE_KEY,
  KLOW_COLUMN_STORAGE_KEY,
  KLOW_COLUMNS,
  KLOW_VISIBILITY_STORAGE_KEY,
  ROOTS_COLUMN_STORAGE_KEY,
  ROOTS_COLUMNS,
  ROOTS_VISIBILITY_STORAGE_KEY,
  UNASSIGNED_COLUMN_STORAGE_KEY,
  UNASSIGNED_COLUMNS,
  UNASSIGNED_VISIBILITY_STORAGE_KEY,
  loadColumnOrder,
  loadColumnVisibility,
} from "../lib/columns"
import {
  computePresetRangeForTab,
  type AdsManagerDatePreset,
} from "../lib/datePresets"
import type {
  MetricFilterBounds,
  MetricFiltersState,
} from "../lib/metricFilters"

export type AdsManagerTab = "roots" | "adw" | "klow" | "brm" | "unassigned"

export interface AdsManagerState {
  activeTab: AdsManagerTab
  page: number
  pageSize: number
  status: string
  search: string
  startDate: string
  endDate: string
  datePreset: AdsManagerDatePreset
  dateDisplayLabel: string
  sortBy: string
  sortOrder: "asc" | "desc"
  selectedAccountIds: string[]
  selectedUserIds: number[]
  metricFilters: MetricFiltersState
  // Per-tab column state
  rootsColumnOrder: string[]
  rootsHiddenColumns: Set<string>
  adwColumnOrder: string[]
  adwHiddenColumns: Set<string>
  klowColumnOrder: string[]
  klowHiddenColumns: Set<string>
  unassignedColumnOrder: string[]
  unassignedHiddenColumns: Set<string>
}

export type AdsManagerAction =
  | { type: "SET_TAB"; tab: AdsManagerTab }
  | { type: "SET_PAGE"; page: number }
  | { type: "SET_PAGE_SIZE"; pageSize: number }
  | { type: "SET_STATUS"; value: string }
  | { type: "SET_SEARCH"; value: string }
  | { type: "SET_SORT"; sortBy: string; columnType: "string" | "number" }
  | { type: "SET_ACCOUNT_IDS"; ids: string[] }
  | { type: "SET_USER_IDS"; ids: number[] }
  | {
      type: "SET_METRIC_FILTER"
      columnId: string
      bounds: MetricFilterBounds | null
    }
  | { type: "CLEAR_METRIC_FILTERS" }
  | {
      type: "SET_PRESET"
      preset: AdsManagerDatePreset
      startDate: string
      endDate: string
      displayLabel: string
    }
  | {
      type: "SET_CUSTOM_RANGE"
      startDate: string
      endDate: string
      displayLabel: string
    }
  | { type: "SET_COLUMN_ORDER"; tab: AdsManagerTab; order: string[] }
  | { type: "TOGGLE_COLUMN_VISIBILITY"; tab: AdsManagerTab; columnId: string }

export interface InitParams {
  urlTab?: string
  urlUserId?: string
  urlAccountId?: string
  urlStartDate?: string
  urlEndDate?: string
  currentUserId?: number
  currentUserRole?: string | null
  /** Same source as tab visibility (`hasNetwork`) — not admin-only for KLOW. */
  networkSlugs?: string[] | null
}

export function createInitialState(params?: InitParams): AdsManagerState {
  // Tab deep links follow per-user network assignment (Admin → Networks), same
  // as tab visibility — not role=admin. BRM / Unassigned stay admin-only.
  const networkUser = {
    role: params?.currentUserRole,
    network_slugs: params?.networkSlugs,
  }

  let activeTab: AdsManagerTab = "roots"
  if (params?.urlTab === "adw" && userHasNetwork(networkUser, "adw")) {
    activeTab = "adw"
  } else if (params?.urlTab === "klow" && userHasNetwork(networkUser, "klow")) {
    activeTab = "klow"
  } else if (params?.urlTab === "roots" && userHasNetwork(networkUser, "roots")) {
    activeTab = "roots"
  } else if (params?.urlTab === "brm" && params?.currentUserRole === "admin") {
    activeTab = "brm"
  } else if (params?.urlTab === "unassigned" && params?.currentUserRole === "admin") {
    activeTab = "unassigned"
  }

  const today = computePresetRangeForTab("today", activeTab)

  const isTeamLeader = params?.currentUserRole === "team_leader"
  const selectedUserIds = params?.urlUserId
    ? [Number(params.urlUserId)]
    : isTeamLeader && params.currentUserId
      ? [params.currentUserId]
      : []
  const selectedAccountIds = params?.urlAccountId
    ? [params.urlAccountId]
    : []

  return {
    activeTab,
    page: 1,
    pageSize: 50,
    status: "all",
    search: "",
    startDate: params?.urlStartDate || today.startDate,
    endDate: params?.urlEndDate || today.endDate,
    datePreset: params?.urlStartDate ? "custom" : "today",
    dateDisplayLabel: params?.urlStartDate ? "Custom" : today.displayLabel,
    sortBy: "cost",
    sortOrder: "desc",
    selectedAccountIds,
    selectedUserIds,
    metricFilters: {},
    rootsColumnOrder: loadColumnOrder(ROOTS_COLUMN_STORAGE_KEY, ROOTS_COLUMNS),
    rootsHiddenColumns: loadColumnVisibility(ROOTS_VISIBILITY_STORAGE_KEY),
    adwColumnOrder: loadColumnOrder(ADW_COLUMN_STORAGE_KEY, ADW_COLUMNS),
    adwHiddenColumns: loadColumnVisibility(ADW_VISIBILITY_STORAGE_KEY),
    klowColumnOrder: loadColumnOrder(KLOW_COLUMN_STORAGE_KEY, KLOW_COLUMNS),
    klowHiddenColumns: loadColumnVisibility(KLOW_VISIBILITY_STORAGE_KEY),
    unassignedColumnOrder: loadColumnOrder(
      UNASSIGNED_COLUMN_STORAGE_KEY,
      UNASSIGNED_COLUMNS,
    ),
    unassignedHiddenColumns: loadColumnVisibility(
      UNASSIGNED_VISIBILITY_STORAGE_KEY,
    ),
  }
}

export function adsManagerReducer(
  state: AdsManagerState,
  action: AdsManagerAction,
): AdsManagerState {
  switch (action.type) {
    case "SET_TAB": {
      if (action.tab === state.activeTab) return state
      const next: AdsManagerState = {
        ...state,
        activeTab: action.tab,
        page: 1,
        search: "",
        sortBy: "cost",
        sortOrder: "desc",
        metricFilters: {},
      }
      if (state.datePreset === "custom") return next
      const range = computePresetRangeForTab(state.datePreset, action.tab)
      return {
        ...next,
        startDate: range.startDate,
        endDate: range.endDate,
        dateDisplayLabel: range.displayLabel,
      }
    }
    case "SET_PAGE":
      return { ...state, page: action.page }
    case "SET_PAGE_SIZE":
      return { ...state, pageSize: action.pageSize, page: 1 }
    case "SET_STATUS":
      return { ...state, status: action.value, page: 1 }
    case "SET_SEARCH":
      return { ...state, search: action.value, page: 1 }
    case "SET_SORT": {
      if (state.sortBy === action.sortBy) {
        return {
          ...state,
          sortOrder: state.sortOrder === "asc" ? "desc" : "asc",
          page: 1,
        }
      }
      return {
        ...state,
        sortBy: action.sortBy,
        sortOrder: action.columnType === "string" ? "asc" : "desc",
        page: 1,
      }
    }
    case "SET_ACCOUNT_IDS":
      return { ...state, selectedAccountIds: action.ids, page: 1 }
    case "SET_USER_IDS":
      return { ...state, selectedUserIds: action.ids, page: 1 }
    case "SET_METRIC_FILTER": {
      const next = { ...state.metricFilters }
      if (action.bounds == null) {
        delete next[action.columnId]
      } else {
        next[action.columnId] = action.bounds
      }
      return { ...state, metricFilters: next, page: 1 }
    }
    case "CLEAR_METRIC_FILTERS":
      if (Object.keys(state.metricFilters).length === 0) return state
      return { ...state, metricFilters: {}, page: 1 }
    case "SET_PRESET":
      return {
        ...state,
        datePreset: action.preset,
        startDate: action.startDate,
        endDate: action.endDate,
        dateDisplayLabel: action.displayLabel,
        page: 1,
      }
    case "SET_CUSTOM_RANGE":
      return {
        ...state,
        datePreset: "custom",
        startDate: action.startDate,
        endDate: action.endDate,
        dateDisplayLabel: action.displayLabel,
        page: 1,
      }
    case "SET_COLUMN_ORDER": {
      const key = columnOrderKey(action.tab)
      return { ...state, [key]: action.order }
    }
    case "TOGGLE_COLUMN_VISIBILITY": {
      const key = hiddenColumnsKey(action.tab)
      const current = state[key] as Set<string>
      const next = new Set(current)
      if (next.has(action.columnId)) {
        next.delete(action.columnId)
      } else {
        next.add(action.columnId)
      }
      return { ...state, [key]: next }
    }
    default:
      return state
  }
}

function columnOrderKey(
  tab: AdsManagerTab,
): "rootsColumnOrder" | "adwColumnOrder" | "klowColumnOrder" | "unassignedColumnOrder" {
  switch (tab) {
    case "roots":
      return "rootsColumnOrder"
    case "adw":
      return "adwColumnOrder"
    case "klow":
      return "klowColumnOrder"
    case "unassigned":
      return "unassignedColumnOrder"
    default:
      return "rootsColumnOrder"
  }
}

function hiddenColumnsKey(
  tab: AdsManagerTab,
): "rootsHiddenColumns" | "adwHiddenColumns" | "klowHiddenColumns" | "unassignedHiddenColumns" {
  switch (tab) {
    case "roots":
      return "rootsHiddenColumns"
    case "adw":
      return "adwHiddenColumns"
    case "klow":
      return "klowHiddenColumns"
    case "unassigned":
      return "unassignedHiddenColumns"
    default:
      return "rootsHiddenColumns"
  }
}
